/**
 * clasificadores/matrizDistribucion.js — Clasificador puro del dashboard
 * Matriz de Distribución de Publicaciones. Réplica del modelo (molde:
 * publicaciones.js / catalogo.js).
 *
 * Regla de cuadrantes = ESPEJO del dashboard (MatrizDistribucionDashboard):
 *   incógnita = 0 ventas en la ventana · ratio vs promedio (excluye
 *   incógnitas): > 1.15 estrella · < 0.85 eventual (perro) · resto moderada.
 *   Métrica: UNIDADES (la default del dashboard).
 *
 * 2 secciones (definición Mariano 12-jul):
 *   promocionar → TRÁFICO, SIN VENTAS, SIN PROMO ACTIVA: la gente mira y no
 *                 compra — el precio efectivo no convence → activar_promo.
 *                 Orden: visitas DESC.
 *                 ⭐ GATE DE RENTABILIDAD (retrofit 12-jul, mismo criterio que
 *                 Estrategia Full): antes de sugerir promo se verifica el
 *                 corte por cascada ADR 032 (producto→categoría→marca→
 *                 proveedor→empresa). Con espacio → se sugiere CON números
 *                 (descuento máx + precio piso); sin datos → se sugiere con
 *                 ⚠; sin espacio → NO se sugiere (declarado).
 *                 ⭐⭐ GATE INFERIDO (15-jul): el margen se calcula con la
 *                 fórmula COMPLETA (comisión/envío cascada cuenta→ecosistema
 *                 + impuestos) y cobra el gasto REAL de ads de la pub. Pub
 *                 que gasta en ads sin vender → bloqueada con motivo
 *                 (pausar_ad, no promo). Los itemIds sin espacio van en
 *                 `gateBloqueados` para la cancelación retroactiva de tareas.
 *   publicitar  → SIN VENTAS, SIN TRÁFICO, SIN PROMO, SIN PUBLICIDAD y CON
 *                 STOCK (sin stock no figura — publicitar sin poder vender es
 *                 tirar plata): el rincón muerto — la publicidad les trae el
 *                 tráfico inicial → activar_ad. Orden: PRECIO DESC (ticket
 *                 más alto primero — decisión Mariano 12-jul) · lotes de 50
 *                 vía cuotaProduccion del tipo.
 * Las que VENDEN no generan tarea en la Matriz (se declaran).
 * (12-jul: completar_datos SALIÓ — la siembra queda en los botones "Sembrar".)
 *
 * Piso de visitas configurable: empresa.umbralesMatriz.pisoVisitas (default 100).
 * Incógnita sin tráfico NO molesta (6.805 pubs — se declara el conteo).
 *
 * Input: { publicaciones (legacy + datosCanal/facetas/_visitas30d),
 *          ventas30d (ordenes de la ventana),
 *          adsItems (ads inscriptos — refuerza la exclusión de publicitar:
 *          faceta conAd OR ad inscripto en campanasItems, cualquier estado),
 *          productos + catalogoLinks + categorias + marcas + proveedores +
 *          productoSourcing + rentabilidadEmpresa (gate de rentabilidad),
 *          config (empresa) }
 */

import { textoGate, construirInfoRentabilidadPorItem, gatePromoConCostosReales, metricasAdsPorItem, gateAdsPrender, textoGateAds } from './gatePromoRentabilidad'
import { estructuraCostosML } from '@core/lib/rentabilidadEstimada'

export const PISO_VISITAS_DEFAULT = 100
const UMBRAL_SUPERIOR = 1.15
const UMBRAL_INFERIOR = 0.85

const esActiva = (p) => p?.status === 'active' || p?.estado === 'activa'
const itemIdDe = (p) => String(p?.itemId || p?.externalId || p?.id || '')

// Mismo criterio que el dashboard: incógnita = sin ventas; ratio vs promedio.
function clasificarCuadrante(unidades, promedio) {
  if (!unidades || unidades <= 0) return 'incognita'
  if (!promedio || promedio <= 0) return 'moderada'
  const ratio = unidades / promedio
  if (ratio > UMBRAL_SUPERIOR) return 'estrella'
  if (ratio < UMBRAL_INFERIOR) return 'perro'
  return 'moderada'
}

// Líneas de una orden — tolera shape cascada (order_items), canónico crudo
// (datosCanal.orderItemsRaw) y legacy (lineas).
function lineasDe(v) {
  const raw = Array.isArray(v?.order_items) ? v.order_items
    : Array.isArray(v?.datosCanal?.orderItemsRaw) ? v.datosCanal.orderItemsRaw
    : null
  if (raw) return raw.map(oi => ({ itemId: oi?.item?.id ?? oi?.itemId ?? null, qty: Number(oi?.quantity ?? oi?.cantidad) || 0 }))
  if (Array.isArray(v?.lineas)) return v.lineas.map(l => ({ itemId: l?.itemId ?? l?.externalItemId ?? null, qty: Number(l?.cantidad ?? l?.quantity) || 0 }))
  return []
}

function baseItem(p, extra) {
  return {
    id: itemIdDe(p),
    itemId: itemIdDe(p),
    _itemId: itemIdDe(p),
    title: p.title ?? p.titulo ?? null,
    price: p.price ?? null,
    permalink: p.permalink ?? null,
    ...extra,
  }
}

export function clasificarMatrizDistribucion({
  publicaciones = [],
  ventas30d = [],
  adsItems = [],
  productos = [],
  catalogoLinks = [],
  categorias = [],
  marcas = [],
  proveedores = [],
  productoSourcing = [],
  rentabilidadEmpresa = null,
  config = {},
  impuestosEmpresa = null,   // loader _impuestos (config fiscal mergeada)
  ecosistema = null,         // doc conocimientoEcosistema/costos-ml (loader)
} = {}) {
  // Ads inscriptos por item (cualquier estado — un ad pausado también cuenta
  // como "ya tiene publicidad": activarlo es trabajo del dashboard Publicidad,
  // no de la Matriz). Cinturón y tiradores sobre la faceta conAd.
  const itemsConAd = new Set()
  for (const ad of adsItems || []) {
    const itemId = ad?.item_id || ad?.itemId || (ad?.id ? String(ad.id).split('_').pop() : null)
    if (itemId) itemsConAd.add(String(itemId))
  }
  const pisoVisitas = Number(config?.umbralesMatriz?.pisoVisitas) > 0
    ? Number(config.umbralesMatriz.pisoVisitas)
    : PISO_VISITAS_DEFAULT

  // ── Rentabilidad: corte por cascada ADR 032 + costo (gate de promos) ──
  // Mismo builder que Estrategia Full — sin datos el gate devuelve 'sin_datos'
  // (se sugiere con ⚠), así que la Matriz sigue funcionando sin catálogo.
  const infoRentPorItem = construirInfoRentabilidadPorItem({
    productos, catalogoLinks, categorias, marcas, proveedores, productoSourcing,
    empresaMin: rentabilidadEmpresa?.rentabilidadMinima ?? null,
  })
  // Gate INFERIDO (15-jul): fórmula completa (cascada cuenta → ecosistema +
  // impuestos) y costos reales vivos (gasto de ads de la pub).
  const estructuraCuenta = estructuraCostosML(ventas30d)
  const metricsAdsMap = metricasAdsPorItem(adsItems)

  // ── Unidades vendidas por item en la ventana ──
  const unidadesPorItem = new Map()
  for (const v of ventas30d) {
    for (const l of lineasDe(v)) {
      if (!l.itemId || l.qty <= 0) continue
      const k = String(l.itemId)
      unidadesPorItem.set(k, (unidadesPorItem.get(k) || 0) + l.qty)
    }
  }

  // ── Promedio de unidades (excluye incógnitas — mismo criterio que la UI) ──
  const activas = publicaciones.filter(esActiva)
  const conVentas = activas.map(p => unidadesPorItem.get(itemIdDe(p)) || 0).filter(u => u > 0)
  const promedio = conVentas.length > 0 ? conVentas.reduce((s, u) => s + u, 0) / conVentas.length : 0

  const promocionar = []
  const publicitar = []
  let venden = 0
  let conPromoYa = 0
  let conAdYa = 0
  let sinStock = 0
  let sinEspacioRentabilidad = 0
  let publicitarAPerdida = 0
  let adsGastanSinVender = 0
  let publicitarSinEspacio = 0
  const gateBloqueadosPromocionar = []   // itemIds sin espacio → cancelación retroactiva de zombies

  for (const p of activas) {
    const itemId = itemIdDe(p)
    const unidades = unidadesPorItem.get(itemId) || 0
    const visitas = p._visitas30d ?? p?.datosCanal?.visitasRecientes?.last30 ?? 0
    const cuadrante = clasificarCuadrante(unidades, promedio)
    const enPromo = p?.facetas?.enPromo === true || (Array.isArray(p.dealIds) && p.dealIds.length > 0)
    const conAd = p?.facetas?.conAd === true || itemsConAd.has(itemId)
    const vendeCuadrante = cuadrante === 'estrella' || cuadrante === 'moderada' || cuadrante === 'perro'

    // Las que venden no generan tarea en la Matriz — se declaran.
    if (vendeCuadrante) { venden++; continue }

    // ── Incógnitas (0 ventas): el piso de tráfico parte las dos aguas ──
    if (visitas >= pisoVisitas) {
      // promocionar: TRÁFICO + sin ventas + sin promo activa + GATE de rentabilidad
      if (enPromo) { conPromoYa++ }
      else {
        const rent = infoRentPorItem.get(itemId) || { costoUnit: null, corte: null }
        // Incógnita = 0 ventas en la ventana → ventasItem null: si la pub
        // tiene gasto de ads sin NINGUNA venta, el gate la bloquea con motivo
        // (candidata a pausar_ad, no a promo).
        const gate = gatePromoConCostosReales({
          precio: p.price, rent,
          metricsAds: metricsAdsMap.get(itemId) || null,
          ventasItem: null,
          categoryId: p?.datosCanal?.categoryId ?? null,
          estructuraCuenta, ecosistema, impuestosEmpresa,
        })
        if (gate.estado === 'sin_espacio') {
          // La promo destruiría la rentabilidad mínima del cliente → NO se
          // sugiere (declarado). Mismo criterio que promocionar_lento de Full.
          if (gate.motivo === 'ads_gastan_sin_vender') adsGastanSinVender++
          else sinEspacioRentabilidad++
          gateBloqueadosPromocionar.push(itemId)
        } else {
          promocionar.push(baseItem(p, {
            _visitas30d: visitas,
            _unidades: 0,
            _gate: gate,
            _motivo: `${visitas.toLocaleString('es-AR')} visitas/30d sin ninguna venta — sin promo activa · ${textoGate(gate)}`,
            metricas: {
              visitas30d: visitas,
              unidades: 0,
              cuadrante: 'incognita',
              gate: { estado: gate.estado, margenActualPct: gate.margenActualPct, cortePct: gate.cortePct, descuentoMaxPct: gate.descuentoMaxPct, precioPiso: gate.precioPiso },
            },
          }))
        }
      }
    } else {
      // publicitar: SIN ventas + SIN tráfico + sin promo + sin publicidad + CON stock
      const stock = Number(p.availableQuantity ?? p.available_quantity) || 0
      if (enPromo) { conPromoYa++ }
      else if (conAd) { conAdYa++ }
      else if (stock <= 0) { sinStock++ }
      // GATE DE ADS (15-jul-2026): margen proyectado con ACOS esperado.
      // Proyectado < 0 → veto (pagar por vender a pérdida); < mínima → no se
      // sugiere (declarado); sin datos → se sugiere con ⚠ (no a ciegas).
      else {
        const rentAd = infoRentPorItem.get(itemId) || { costoUnit: null, corte: null }
        const gateAd = gateAdsPrender({
          precio: p.price,
          margenReal: Number.isFinite(p?._rentabilidadInferida?.margenPctProm) ? p._rentabilidadInferida.margenPctProm : null,
          costoUnit: rentAd.costoUnit, corte: rentAd.corte,
          categoryId: p?.datosCanal?.categoryId ?? null,
          estructuraCuenta, ecosistema, impuestosEmpresa,
          benchmarksEmpresa: config?._benchmarksML || null,
        })
        if (gateAd.estado === 'veto_perdida') { publicitarAPerdida++ }
        else if (gateAd.estado === 'sin_espacio') { publicitarSinEspacio++ }
        else {
          publicitar.push(baseItem(p, {
            _visitas30d: visitas,
            _unidades: 0,
            _stock: stock,
            _gateAds: gateAd,
            _motivo: `Sin ventas y solo ${visitas} visitas/30d — sin promo ni publicidad: necesita visibilidad · ${textoGateAds(gateAd)}`,
            metricas: { visitas30d: visitas, unidades: 0, stock, cuadrante: 'incognita', gateAds: { estado: gateAd.estado, margenProyectadoPct: gateAd.margenProyectadoPct, acosEsperadoPct: gateAd.acosEsperadoPct } },
          }))
        }
      }
    }
  }

  promocionar.sort((a, b) => (b._visitas30d ?? 0) - (a._visitas30d ?? 0))
  // Precio DESC (ticket más alto primero) — el lote 1 maximiza facturación
  // potencial por conversión. Visitas como desempate.
  publicitar.sort((a, b) => (Number(b.price) || 0) - (Number(a.price) || 0) || (b._visitas30d ?? 0) - (a._visitas30d ?? 0))

  return {
    secciones: { promocionar, publicitar },
    analisis: {
      kpis: {
        promedioUnidades: promedio,
        promocionar: promocionar.length,
        publicitar: publicitar.length,
      },
    },
    excluidos: {
      pisoVisitasEfectivo: pisoVisitas,
      promedioUnidades: promedio,
      venden,
      conPromoYa,
      conAdYa,
      sinStock,
      publicitarAPerdida,   // flywheel: con ACOS esperado quedaría a pérdida (veto)
      publicitarSinEspacio, // gate ads 15-jul: proyectado abajo de la mínima
      sinEspacioRentabilidad,
      adsGastanSinVender,   // gate 15-jul: gastan en ads sin vender — pausar_ad, no promo
    },
    // Ítems bloqueados por el gate inferido → el motor cancela (con motivo)
    // las tareas abiertas del mismo tipo creadas cuando el margen daba.
    gateBloqueados: { promocionar: gateBloqueadosPromocionar },
  }
}
